import type { ReactNode } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";

interface CompactDataCardProps {
    title: string;
    subtitle?: ReactNode;
    badges?: ReactNode;
    children?: ReactNode;
    footer?: ReactNode;
    selected?: boolean;
    onClick?: () => void;
    onDoubleClick?: () => void;
}

export default function CompactDataCard({
    title,
    subtitle,
    badges,
    children,
    footer,
    selected = false,
    onClick,
    onDoubleClick,
}: CompactDataCardProps) {
    return (
        <Paper
            elevation={0}
            className="compact-data-card"
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            sx={{
                cursor: onClick || onDoubleClick ? "pointer" : "default",
                border: selected
                    ? "1px solid rgba(15, 118, 110, 0.24)"
                    : "1px solid rgba(216, 225, 235, 0.9)",
                backgroundColor: selected
                    ? "rgba(15, 118, 110, 0.06)"
                    : "rgba(255, 255, 255, 0.72)",
                boxShadow: selected
                    ? "0 16px 32px rgba(15, 118, 110, 0.10)"
                    : "0 8px 22px rgba(16, 42, 67, 0.04)",
            }}
        >
            <Stack spacing={1.5}>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>

                    {badges && (
                        <Stack spacing={0.75} sx={{ alignItems: "flex-end", flexShrink: 0 }}>
                            {badges}
                        </Stack>
                    )}
                </Stack>

                {children && <Box sx={{ display: "grid", gap: 1 }}>{children}</Box>}

                {footer && <Box>{footer}</Box>}
            </Stack>
        </Paper>
    );
}
